import { SubmitHelpers } from '@area2k/use-form';
import useModal from '@area2k/use-modal';
import { isEqual } from 'lodash';
import { useState } from 'react';

import Button from '@/components/Button';
import Card from '@/components/Card';
import ConfirmationModal from '@/components/ConfirmationModal';
import FormElement from '@/components/FormElement';
import Modal from '@/components/Modal';
import Option from '@/components/Option';
import Stack from '@/components/Stack';
import Form from '@/form';
import FormColumns from '@/form/FormColumns';
import MaskedInputField from '@/form/MaskedInputField';
import TextField from '@/form/TextField';
import TextSelectField from '@/form/TextSelectField';
import { GenderEnum, LanguageEnum, SmartphoneTypeEnum } from '@/types/graphql';
import { CONFIRM_MSG } from '@/util/constants';
import {
  WorkerDetail,
  WorkersV2API,
  UpdateWorkerProfilePayload,
} from '@/services/workersV2API';

import EmailTakenAlert, { ConflictingWorker } from './EmailTakenAlert';

type Props = {
  worker: WorkerDetail;
  onClose: () => void;
  onSuccess: () => void;
};

type FormValues = {
  email: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  gender: GenderEnum;
  heardFrom: string | null;
  language: LanguageEnum;
  phoneNumber: string;
  ownsCar: boolean;
  secondaryLanguage: LanguageEnum | null;
  smartphoneType: SmartphoneTypeEnum;
  dateOfBirth: string;
  hasSsn: boolean;
};

const smartPhoneTypeOptions = [
  { label: 'Android', value: SmartphoneTypeEnum.ANDROID },
  { label: 'IOS', value: SmartphoneTypeEnum.IOS },
  { label: 'Other', value: SmartphoneTypeEnum.OTHER },
];

const genderOptions = [
  { label: 'Male', value: GenderEnum.MALE },
  { label: 'Female', value: GenderEnum.FEMALE },
  { label: 'Non-Binary', value: GenderEnum.NON_BINARY },
  { label: 'Unspecified', value: GenderEnum.UNSPECIFIED },
];

const genderDbToEnum: Record<string, GenderEnum> = {
  M: GenderEnum.MALE,
  F: GenderEnum.FEMALE,
  X: GenderEnum.NON_BINARY,
  U: GenderEnum.UNSPECIFIED,
};

const languageDbToEnum: Record<string, LanguageEnum> = {
  en: LanguageEnum.ENGLISH,
  es: LanguageEnum.SPANISH,
};

const smartphoneDbToEnum: Record<string, SmartphoneTypeEnum> = {
  android: SmartphoneTypeEnum.ANDROID,
  ios: SmartphoneTypeEnum.IOS,
  other: SmartphoneTypeEnum.OTHER,
};

const toGenderEnum = (val: string | null | undefined): GenderEnum =>
  (val &&
    (genderDbToEnum[val] || GenderEnum[val as keyof typeof GenderEnum])) ||
  GenderEnum.UNSPECIFIED;

const toLanguageEnum = (val: string | null | undefined): LanguageEnum =>
  (val &&
    (languageDbToEnum[val] ||
      LanguageEnum[val as keyof typeof LanguageEnum])) ||
  LanguageEnum.ENGLISH;

const toSmartphoneEnum = (val: string | null | undefined): SmartphoneTypeEnum =>
  (val &&
    (smartphoneDbToEnum[val] ||
      SmartphoneTypeEnum[val as keyof typeof SmartphoneTypeEnum])) ||
  SmartphoneTypeEnum.ANDROID;

const genderEnumToDb: Record<GenderEnum, string> = {
  [GenderEnum.MALE]: 'M',
  [GenderEnum.FEMALE]: 'F',
  [GenderEnum.NON_BINARY]: 'X',
  [GenderEnum.UNSPECIFIED]: 'U',
};

const languageEnumToDb: Record<LanguageEnum, string> = {
  [LanguageEnum.ENGLISH]: 'en',
  [LanguageEnum.SPANISH]: 'es',
};

const smartphoneEnumToDb: Record<SmartphoneTypeEnum, string> = {
  [SmartphoneTypeEnum.ANDROID]: 'android',
  [SmartphoneTypeEnum.IOS]: 'ios',
  [SmartphoneTypeEnum.OTHER]: 'other',
};

const UpdateProfileModal = ({ worker, onClose, onSuccess }: Props) => {
  const initialValues: FormValues = {
    email: worker.user?.email || '',
    firstName: worker.user?.first_name || '',
    middleName: worker.user?.middle_name || null,
    lastName: worker.user?.last_name || '',
    gender: toGenderEnum(worker.gender),
    heardFrom: worker.heard_from || null,
    dateOfBirth: worker.user?.date_of_birth
      ? new Date(worker.user.date_of_birth).toISOString().split('T')[0]
      : '',
    language: toLanguageEnum(worker.user?.language),
    phoneNumber: worker.user?.phone_number || '',
    ownsCar: worker.owns_car || false,
    secondaryLanguage: worker.user?.secondary_language
      ? toLanguageEnum(worker.user.secondary_language)
      : null,
    smartphoneType: toSmartphoneEnum(worker.user?.smartphone_type),
    hasSsn: worker.has_ssn || false,
  };

  const [workerValues, setWorkerValues] = useState<FormValues>(initialValues);
  const [loading, setLoading] = useState(false);
  const [emailTaken, setEmailTaken] = useState<{
    message: string;
    conflictingWorker: ConflictingWorker;
  } | null>(null);
  const [genericError, setGenericError] = useState<string | null>(null);

  const handleWorkerValuesChange = (
    { value }: { value: any },
    id: keyof FormValues,
  ) => setWorkerValues({ ...workerValues, [id]: value });

  const [showConfirmationModal, hideConfirmationModal] = useModal(
    ({ values, submitHelpers }: any) => (
      <ConfirmationModal
        acceptAction={() => handleSubmit(values, submitHelpers)}
        acceptButtonLabel="Confirm"
        bodyContentText={CONFIRM_MSG}
        denyAction={() => {}}
        denyButtonLabel="Cancel"
        hideModal={hideConfirmationModal}
        title="Confirmation"
        loading={loading}
      />
    ),
    [workerValues, initialValues],
  );

  const handleSubmit = async (
    values: FormValues,
    _submitHelpers: SubmitHelpers,
  ) => {
    setLoading(true);
    setEmailTaken(null);
    setGenericError(null);
    try {
      if (isEqual(workerValues, initialValues)) {
        return onClose();
      }

      const {
        dateOfBirth,
        email,
        firstName,
        middleName,
        lastName,
        gender,
        heardFrom,
        language,
        phoneNumber,
        secondaryLanguage,
        smartphoneType,
      } = values;

      const { ownsCar, hasSsn } = workerValues;

      const payload: UpdateWorkerProfilePayload = {
        first_name: firstName,
        middle_name: middleName,
        last_name: lastName,
        email,
        date_of_birth: dateOfBirth,
        gender: gender ? genderEnumToDb[gender] : null,
        language: language ? languageEnumToDb[language] : null,
        secondary_language: secondaryLanguage
          ? languageEnumToDb[secondaryLanguage]
          : null,
        smartphone_type: smartphoneType
          ? smartphoneEnumToDb[smartphoneType]
          : null,
        heard_from: heardFrom,
        owns_car: ownsCar,
        has_ssn: hasSsn,
        phone_number: phoneNumber,
      };

      const result = await WorkersV2API.updateWorkerProfile(worker.id, payload);

      if (result.success) {
        onSuccess();
        onClose();
        return;
      }

      const taken = result.errors.find((e) => e.code === 'taken');
      if (taken) {
        setEmailTaken({
          message: taken.message,
          conflictingWorker: taken.conflicting_worker ?? null,
        });
      } else {
        setGenericError(
          result.errors[0]?.message ?? 'Failed to update profile.',
        );
      }
    } catch (err) {
      setGenericError(
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      disableClickout
      size="lg"
      title="Update Profile"
      onRequestClose={onClose}
    >
      <Card.Section>
        {emailTaken && (
          <EmailTakenAlert
            message={emailTaken.message}
            conflictingWorker={emailTaken.conflictingWorker}
          />
        )}
        {genericError && !emailTaken && (
          <EmailTakenAlert message={genericError} conflictingWorker={null} />
        )}
        <Form
          initialValues={initialValues}
          onSubmit={async (values: FormValues, submitHelpers: SubmitHelpers) =>
            showConfirmationModal({ values, submitHelpers })
          }
        >
          <FormColumns layout="triple">
            <TextField
              autoFocus
              required
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'firstName');
              }}
              fieldId="firstName"
              label="First Name"
            />
            <TextField
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'middleName');
              }}
              fieldId="middleName"
              label="Middle Name"
            />
            <TextField
              required
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'lastName');
              }}
              fieldId="lastName"
              label="Last Name"
            />
          </FormColumns>
          <FormColumns>
            <TextField
              required
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'dateOfBirth');
              }}
              fieldId="dateOfBirth"
              label="Date of Birth"
              type="date"
            />
            <TextSelectField
              required
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'gender');
              }}
              fieldId="gender"
              label="Gender"
              options={genderOptions}
            />
          </FormColumns>
          <FormColumns>
            <MaskedInputField
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'phoneNumber');
              }}
              fieldId="phoneNumber"
              incompletemessage="Must be a valid phone number"
              label="Phone Number"
              mask="(000) 000-0000"
              placeholder={
                workerValues.phoneNumber
                  ? workerValues.phoneNumber
                  : '(555) 555-5555'
              }
              type="tel"
            />
            <TextField
              required
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'email');
              }}
              fieldId="email"
              label="Email Address"
            />
          </FormColumns>
          <FormColumns>
            <TextSelectField
              required
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'smartphoneType');
              }}
              fieldId="smartphoneType"
              label="Phone Type"
              options={smartPhoneTypeOptions}
            />
            <FormElement label="Important Information">
              <Stack vertical>
                <Option
                  appearance="checkbox"
                  checked={
                    worker.tax_type === 'w2' ? false : workerValues.hasSsn
                  }
                  disabled={worker.tax_type === 'w2'}
                  id="hasSsn"
                  label="Eligible for W2 employment"
                  onChange={() =>
                    handleWorkerValuesChange(
                      { value: !workerValues.hasSsn },
                      'hasSsn',
                    )
                  }
                />
                <Option
                  appearance="checkbox"
                  checked={workerValues.ownsCar}
                  id="ownsCar"
                  label="Access To Reliable Transportation?"
                  onChange={() =>
                    handleWorkerValuesChange(
                      { value: !workerValues.ownsCar },
                      'ownsCar',
                    )
                  }
                />
              </Stack>
            </FormElement>
          </FormColumns>
          <Stack justify="end">
            <Button
              a11yLabel="Submit form"
              id="btn-submit"
              isLoading={loading}
              label="Save"
              type="submit"
            />
          </Stack>
        </Form>
      </Card.Section>
    </Modal>
  );
};

export default UpdateProfileModal;
